import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Alert } from "@amzn/awsui-components-react/polaris";

import { RootState } from "../app/rootReducer";
import { VALIDATION_STATUS_NOTIFICATION_KEY } from "../notification/notificationConnector";

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
const VALIDATION_ROUTE_PREFIX = "/validation";

function isStale(
  earliestValidationRunAtMs: number | undefined,
  nowMs: number
): boolean {
  if (earliestValidationRunAtMs === undefined) return false;
  return nowMs - earliestValidationRunAtMs > TWENTY_FOUR_HOURS_MS;
}

/**
 * Standalone Cloudscape `Alert` that warns when the earliest known
 * validation-run timestamp is older than 24 hours. Dismissal is
 * per-timestamp: a new run resets the dismiss state so the next
 * stale window is announced again.
 */
export const StaleDataAlert: React.FC = () => {
  // Rendered above <Routes> in the App Shell, so it mounts on every
  // path. Gate on the current route so the validation-specific alert
  // doesn't surface on /search, /baseline, /launch, etc. after the
  // user has visited the dashboard.
  const location = useLocation();
  const onValidationRoute = location.pathname.startsWith(
    VALIDATION_ROUTE_PREFIX
  );

  const earliestValidationRunAtMs = useSelector(
    (state: RootState) =>
      state.statusOverviewAggregation.earliestValidationRunAtMs
  );

  const hasActiveValidationStatus = useSelector(
    (state: RootState) =>
      state.notification.notificationItems[
        VALIDATION_STATUS_NOTIFICATION_KEY
      ] !== undefined
  );

  const [dismissedForTimestamp, setDismissedForTimestamp] = useState<
    number | undefined
  >(undefined);

  useEffect(() => {
    if (
      dismissedForTimestamp !== undefined &&
      dismissedForTimestamp !== earliestValidationRunAtMs
    ) {
      setDismissedForTimestamp(undefined);
    }
  }, [earliestValidationRunAtMs, dismissedForTimestamp]);

  const stale = isStale(earliestValidationRunAtMs, Date.now());
  const dismissed = dismissedForTimestamp === earliestValidationRunAtMs;

  if (!onValidationRoute || !stale || dismissed || hasActiveValidationStatus) {
    return null;
  }

  return (
    <Alert
      data-testid="validation-dashboard-stale-data-alert"
      type="warning"
      header="Stale Validation Data"
      dismissible
      i18nStrings={{
        warningIconAriaLabel: "Warning",
        dismissAriaLabel: "Dismiss stale validation data alert",
      }}
      onDismiss={() => setDismissedForTimestamp(earliestValidationRunAtMs)}
    >
      Some validation data is older than 24 hours and may be stale. Run
      validation to refresh.
    </Alert>
  );
};

export default StaleDataAlert;
